import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AI_FEEDBACK, AI_FEEDBACK_LABELS, WALL_MODES, WALL_MODE_LABELS, type AiFeedback, type WallMode } from '@raro/domain';
import { Button, EmptyState, Field, Input, Label, MachineWallBrand, Notice, Select, Tag, Title } from '@raro/ui';
import { useState } from 'react';
import { useAiStatus } from '../lib/ai';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import type { SerendipityCard, WallEventRow, WallSession } from '../lib/types';

/** Controle remoto da parede: cria sessões, troca de modo, foca, escolhe projeto. */
export function WallControlPage() {
  const qc = useQueryClient();
  const status = useAiStatus();
  const sessions = useQuery({ queryKey: ['wall-sessions'], queryFn: () => api.get<WallSession[]>('/wall/sessions') });
  const projects = useQuery({ queryKey: ['wall-projects'], queryFn: () => api.get<{ id: string; name: string; organization: string }[]>('/wall/projects') });
  const events = useQuery({ queryKey: ['wall-events'], queryFn: () => api.get<WallEventRow[]>('/wall/events'), refetchInterval: 15_000 });
  const cards = useQuery({ queryKey: ['serendipity'], queryFn: () => api.get<SerendipityCard[]>('/wall/serendipity') });
  const [name, setName] = useState('Parede do escritório');
  const [focusText, setFocusText] = useState({ title: '', text: '' });
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['wall-sessions'] }); void qc.invalidateQueries({ queryKey: ['serendipity'] }); };
  const create = useMutation({ mutationFn: () => api.post('/wall/sessions', { name }), onSuccess: invalidate });
  const update = useMutation({ mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) => api.patch(`/wall/sessions/${id}`, body), onSuccess: invalidate });
  const scan = useMutation({ mutationFn: () => api.post<{ scanned: number; pairs: number; cards: unknown[] }>('/wall/serendipity/scan'), onSuccess: invalidate });
  const feedback = useMutation({ mutationFn: ({ id, f }: { id: string; f: AiFeedback }) => api.post(`/wall/serendipity/${id}/feedback`, { feedback: f }), onSuccess: invalidate });
  const origin = window.location.origin;

  return (
    <div className="rr-page rr-stack rr-stack--7">
      <header className="rr-stack rr-stack--2">
        <MachineWallBrand size="lg" />
        <Title>o que as paredes mostram agora.</Title>
        <p className="rr-muted">duas TVs, uma sessão. ambient provoca; foco domina; projeto acompanha a reunião; serendipidade conecta o distante.</p>
      </header>

      <section className="rr-section">
        <div className="rr-section__head"><Label>Sessões</Label></div>
        <div className="rr-row" style={{ alignItems: 'flex-end' }}>
          <Field label="Nova sessão" htmlFor="ws-name"><Input id="ws-name" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Button variant="primary" onClick={() => create.mutate()} disabled={create.isPending}>criar</Button>
        </div>
        {sessions.isSuccess && sessions.data.length === 0 && <EmptyState title="nenhuma parede ainda." />}
        {sessions.data?.map((s) => (
          <div key={s.id} className="rr-card rr-stack rr-stack--4">
            <div className="rr-row rr-row--between rr-row--wrap">
              <span className="rr-heading">{s.name}</span>
              <span className="rr-row">
                {WALL_MODES.map((m) => <Button key={m} size="sm" variant={s.mode === m ? 'primary' : 'ghost'} onClick={() => update.mutate({ id: s.id, mode: m })}>{WALL_MODE_LABELS[m]}</Button>)}
              </span>
            </div>
            <div className="rr-row rr-row--wrap" style={{ gap: 'var(--rr-space-4)', fontSize: 'var(--rr-text-xs)' }}>
              {(['left', 'right', 'single'] as const).map((sc) => (
                <a key={sc} className="rr-action" href={`${origin}/wall?session=${s.id}&token=${s.token}&screen=${sc}`} target="_blank" rel="noreferrer">abrir TV {sc} →</a>
              ))}
            </div>
            {s.mode === 'project' && (
              <Field label="Projeto na parede" htmlFor={`p-${s.id}`}>
                <Select id={`p-${s.id}`} value={s.projectId ?? ''} onChange={(e) => update.mutate({ id: s.id, projectId: e.target.value || null })}>
                  <option value="">— escolha —</option>
                  {projects.data?.map((p) => <option key={p.id} value={p.id}>{p.organization} · {p.name}</option>)}
                </Select>
              </Field>
            )}
            {s.mode === 'focus' && (
              <div className="rr-stack rr-stack--3">
                <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>em foco agora: {s.focus ? (s.focus.title ?? s.focus.text ?? s.focus.kind) : 'nada'}</span>
                <div className="rr-row" style={{ alignItems: 'flex-end', gap: 'var(--rr-space-3)' }}>
                  <Field label="Título" htmlFor={`ft-${s.id}`}><Input id={`ft-${s.id}`} value={focusText.title} onChange={(e) => setFocusText({ ...focusText, title: e.target.value })} placeholder="o que precisamos enxergar?" /></Field>
                  <Field label="Texto" htmlFor={`fx-${s.id}`}><Input id={`fx-${s.id}`} value={focusText.text} onChange={(e) => setFocusText({ ...focusText, text: e.target.value })} /></Field>
                  <Button size="sm" variant="primary" disabled={!focusText.title} onClick={() => update.mutate({ id: s.id, focus: { kind: 'text', title: focusText.title, text: focusText.text } })}>focar</Button>
                </div>
                <div className="rr-row rr-row--wrap" style={{ gap: 6 }}>
                  {events.data?.slice(0, 6).map((e) => <Button key={e.id} size="sm" variant="quiet" onClick={() => update.mutate({ id: s.id, focus: e.refType && e.refId && e.refType !== 'serendipity' ? { kind: 'entity', entityType: e.refType, id: e.refId } : e.refType === 'serendipity' ? { kind: 'serendipity', id: e.refId } : { kind: 'text', title: e.title, text: e.text ?? '' } })}>{e.title.slice(0, 40)}…</Button>)}
                </div>
              </div>
            )}
          </div>
        ))}
      </section>

      <section className="rr-section">
        <div className="rr-section__head">
          <Label>Serendipidade</Label>
          <Button size="sm" variant="primary" onClick={() => scan.mutate()} disabled={!status.data?.llm || scan.isPending}>{scan.isPending ? 'procurando…' : 'procurar conexões →'}</Button>
        </div>
        {status.data && !status.data.llm && <Notice tone="error">sem modelo configurado: a serendipidade precisa de um LLM para julgar as conexões.</Notice>}
        {scan.isSuccess && <Notice>{scan.data.scanned} itens · {scan.data.pairs} pares avaliados · {scan.data.cards.length} conexão(ões) nova(s).</Notice>}
        {cards.isSuccess && cards.data.length === 0 && <EmptyState title="nenhuma conexão inesperada ainda." />}
        <div className="rr-stack rr-stack--3">
          {cards.data?.map((c) => (
            <div key={c.id} className="rr-contra" style={{ gridTemplateColumns: '1fr' }}>
              <div className="rr-row rr-row--wrap" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}><b style={{ color: 'var(--rr-text)' }}>{c.aLabel}</b> × <b style={{ color: 'var(--rr-text)' }}>{c.bLabel}</b> <span>· {c.method} · relevância {Math.round((c.relevance ?? 0) * 100)}% · {formatDate(c.createdAt)}</span></div>
              <p className="rr-heading">{c.pattern}</p>
              <p className="rr-muted" style={{ fontSize: 'var(--rr-text-sm)' }}>{c.why}</p>
              {c.question && <p style={{ fontSize: 'var(--rr-text-sm)' }}><span className="rr-arrow">→</span> {c.question}</p>}
              <div className="rr-feedback">{AI_FEEDBACK.map((f) => <button key={f} type="button" aria-pressed={c.feedback === f} onClick={() => feedback.mutate({ id: c.id, f })}>{AI_FEEDBACK_LABELS[f]}</button>)}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="rr-section">
        <Label>Feed da parede</Label>
        <div className="rr-list">
          {events.data?.slice(0, 20).map((e) => <div key={e.id} className="rr-list-row" style={{ gridTemplateColumns: '1fr auto' }}><div><div className="rr-list-row__title">{e.title}</div><div className="rr-list-row__meta"><Tag tone="outline">{e.kind}</Tag><span>{e.payload.organization}{e.payload.project ? ` · ${e.payload.project}` : ''}</span></div></div><span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{formatDate(e.createdAt)}</span></div>)}
        </div>
      </section>
    </div>
  );
}
